import React, {useMemo} from 'react';
import SearchableSelect from './SearchableSelect';

/**
 * Shared phone country code selector with flags.
 * Single source of truth for flag rendering across form, settings, auth, etc.
 *
 * Display format: [flag] + | {digits}
 * User types digits only — the "+" is a static visual prefix.
 *
 * @param countries     — object/array of country data from API (code, name, phoneCode)
 * @param value         — selected country code (ISO A2)
 * @param onSelect      — (code) => void, called with selected country code
 * @param placeholder   — placeholder text
 * @param error         — error state flag
 */
export default function PhoneCodeSelect({countries, value, onSelect, placeholder = '1', error = false}) {
    const options = useMemo(() => {
        if (!countries) return [];
        return Object.values(countries)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(c => ({
                value: c.code,
                label: String(c.phoneCode),
                name: c.name,
                flag: `/_core/assets/common/css/flags/4x3/${c.code.toLowerCase()}.svg`,
            }));
    }, [countries]);

    // Max length = longest phone code in the dataset (no hardcoding)
    const maxLength = useMemo(
        () => options.reduce((max, o) => Math.max(max, o.label.length), 0) || undefined,
        [options]
    );

    return (
        <SearchableSelect
            options={options}
            value={value || ''}
            placeholder={placeholder}
            error={error}
            onSelect={onSelect}
            getLabel={o => o.label}
            maxLength={maxLength}
            filterInput={val => val.replace(/[^0-9]/g, '')}
            renderPrefix={o => (
                <span className="resume-phone-prefix">
                    <img src={o.flag} alt="" className="resume-select-dropdown__flag" />
                    <span className="resume-phone-prefix__sign">+</span>
                </span>
            )}
            renderOption={o => (
                <span className="resume-phone-option">
                    <img src={o.flag} alt="" className="resume-phone-option__flag" />
                    <span className="resume-phone-option__code">+{o.label}</span>
                    <span className="resume-phone-option__name">{o.name}</span>
                </span>
            )}
        />
    );
}
